import { useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { useArtistWebSocket } from "@/hooks/useWebSocket";
import { Radio, Music, Zap } from "lucide-react";

/**
 * Component that listens for real-time artist notifications and shows toasts
 */
const ArtistNotifications = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleNotification = useCallback((data) => {
    switch (data.type) {
      case "track_up_next":
        toast.info(
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
              <Zap className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <p className="font-semibold">You&apos;re Up Next!</p>
              <p className="text-sm text-gray-400">&quot;{data.track_name}&quot; is next in queue</p>
            </div>
          </div>,
          {
            duration: 10000,
            action: {
              label: "View Queue",
              onClick: () => navigate(`/queue/${data.reviewer_id}`)
            }
          }
        );
        break;

      case "your_track_now_playing":
        toast.success(
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center animate-pulse">
              <Music className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="font-semibold">🎵 Your Track is Playing!</p>
              <p className="text-sm text-gray-400">&quot;{data.track_name}&quot; is being reviewed NOW</p>
            </div>
          </div>,
          {
            duration: 15000,
            action: {
              label: "Watch Now",
              onClick: () => navigate(`/queue/${data.reviewer_id}`)
            }
          }
        );
        
        // Also show a browser notification if permitted
        if (Notification.permission === "granted") {
          new Notification("🎵 Your Track is Being Reviewed!", {
            body: `"${data.track_name}" is now playing!`,
            icon: "/favicon.ico"
          });
        }
        break;

      case "reviewer_went_live":
        toast(
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
              <Radio className="w-5 h-5 text-red-400 animate-pulse" />
            </div>
            <div>
              <p className="font-semibold">🔴 Reviewer is LIVE!</p>
              <p className="text-sm text-gray-400">{data.reviewer_name} just went live</p>
            </div>
          </div>,
          {
            duration: 10000,
            action: {
              label: "Submit Track",
              onClick: () => navigate(`/artist/submit/${data.reviewer_id}`)
            }
          }
        );
        
        // Browser notification
        if (Notification.permission === "granted") {
          new Notification(`🔴 ${data.reviewer_name} is LIVE!`, {
            body: "Submit your track now for a live review!",
            icon: "/favicon.ico"
          });
        }
        break;

      default:
        // Unknown notification
    }
  }, [navigate]);

  // Connect to WebSocket if user is an artist
  useArtistWebSocket(
    user?.role === "artist" ? user.id : null,
    handleNotification
  );

  // Request notification permission on mount
  useEffect(() => {
    if (user?.role === "artist" && "Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, [user]);

  return null; // This component doesn't render anything visible
};

export default ArtistNotifications;
